import React, { useState } from 'react';
import { ChevronDown, UserPlus, Mail, Calendar, CheckCircle, XCircle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { adminApiService } from '../services/adminApi';

type TabType = 'all' | 'admins' | 'candidates' | 'companies' | 'recruiters';
type SortType = 'newest' | 'oldest';

interface User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    isActive: boolean;
    createdAt: string;
    profileCompleted: boolean;
}

/**
 * User Management Page
 * Admin page to manage all platform users with invite admin functionality
 * Route: /admin/user-management
 */
const UserManagement: React.FC = () => {
    const [activeTab, setActiveTab] = useState<TabType>('admins');
    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [sortBy, setSortBy] = useState<SortType>('newest');
    const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

    // Invite Admin Form State
    const [inviteFormData, setInviteFormData] = useState({
        firstName: '',
        lastName: '',
        email: ''
    });
    const [inviteError, setInviteError] = useState<string | null>(null);
    const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
    const [inviteLoading, setInviteLoading] = useState(false);

    const handleInviteInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setInviteFormData(prev => ({ ...prev, [name]: value }));
        if (inviteError) setInviteError(null);
    };

    const handleInviteAdmin = async (e: React.FormEvent) => {
        e.preventDefault();
        setInviteError(null);
        setInviteSuccess(null);

        try {
            setInviteLoading(true);
            await adminApiService.createAdmin({
                firstName: inviteFormData.firstName,
                lastName: inviteFormData.lastName,
                email: inviteFormData.email
            });

            setInviteSuccess("Admin invitation sent! They will receive an email to set up their password.");
            setInviteFormData({
                firstName: '',
                lastName: '',
                email: ''
            });

            setTimeout(() => {
                setShowInviteModal(false);
                setInviteSuccess(null);
            }, 2000);

        } catch (err: any) {
            setInviteError(err.message || "Failed to send admin invitation");
        } finally {
            setInviteLoading(false);
        }
    };

    const toggleCardExpansion = (id: string) => {
        const newExpanded = new Set(expandedCards);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedCards(newExpanded);
    };

    const getStatusBadge = (isActive: boolean, profileCompleted: boolean) => {
        if (!profileCompleted) {
            return (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded border text-xs font-medium bg-yellow-50 text-yellow-800 border-yellow-200">
                    <Mail size={12} />
                    Pending Setup
                </span>
            );
        }
        if (isActive) {
            return (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded border text-xs font-medium bg-green-50 text-green-800 border-green-200">
                    <CheckCircle size={12} />
                    Active
                </span>
            );
        }
        return (
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded border text-xs font-medium bg-red-50 text-red-800 border-red-200">
                <XCircle size={12} />
                Inactive
            </span>
        );
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const getRoleColor = (role: string) => {
        switch (role) {
            case 'hush_admin':
                return 'bg-purple-50 text-purple-800 border-purple-200';
            case 'candidate':
                return 'bg-blue-50 text-blue-800 border-blue-200';
            case 'recruiter':
                return 'bg-green-50 text-green-800 border-green-200';
            case 'company':
                return 'bg-orange-50 text-orange-800 border-orange-200';
            default:
                return 'bg-gray-50 text-gray-800 border-gray-200';
        }
    };

    const getRoleLabel = (role: string) => {
        if (role === 'hush_admin') return 'Admin';
        return role.charAt(0).toUpperCase() + role.slice(1);
    };

    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab);
    };

    return (
        <AdminLayout
            headerTitle="User Management"
            headerSubtitle="Manage all users across the platform"
        >
            {/* White Container with Tabs and Content */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-[#EDEDED] min-h-[calc(100vh-140px)] relative">
                            {/* Tabs */}
                            <div className="flex items-center gap-8 border-b border-[#EDEDED] mb-6">
                                <button
                                    onClick={() => handleTabChange('all')}
                                    className={`pb-4 text-sm font-semibold relative ${
                                        activeTab === 'all' ? 'text-[#015FA6]' : 'text-[#757575] hover:text-[#0B111E]'
                                    }`}
                                >
                                    All Users
                                    {activeTab === 'all' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#015FA6] rounded-t-full" />}
                                </button>
                                <button
                                    onClick={() => handleTabChange('admins')}
                                    className={`pb-4 text-sm font-semibold relative ${
                                        activeTab === 'admins' ? 'text-[#015FA6]' : 'text-[#757575] hover:text-[#0B111E]'
                                    }`}
                                >
                                    Admins
                                    {activeTab === 'admins' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#015FA6] rounded-t-full" />}
                                </button>
                                <button
                                    onClick={() => handleTabChange('candidates')}
                                    className={`pb-4 text-sm font-semibold relative ${
                                        activeTab === 'candidates' ? 'text-[#015FA6]' : 'text-[#757575] hover:text-[#0B111E]'
                                    }`}
                                >
                                    Candidates
                                    {activeTab === 'candidates' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#015FA6] rounded-t-full" />}
                                </button>
                                <button
                                    onClick={() => handleTabChange('companies')}
                                    className={`pb-4 text-sm font-semibold relative ${
                                        activeTab === 'companies' ? 'text-[#015FA6]' : 'text-[#757575] hover:text-[#0B111E]'
                                    }`}
                                >
                                    Companies
                                    {activeTab === 'companies' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#015FA6] rounded-t-full" />}
                                </button>
                                <button
                                    onClick={() => handleTabChange('recruiters')}
                                    className={`pb-4 text-sm font-semibold relative ${
                                        activeTab === 'recruiters' ? 'text-[#015FA6]' : 'text-[#757575] hover:text-[#0B111E]'
                                    }`}
                                >
                                    Recruiters
                                    {activeTab === 'recruiters' && <div className="absolute bottom-0 left-0 w-full h-0.5 bg-[#015FA6] rounded-t-full" />}
                                </button>
                            </div>

                            {/* Action Bar */}
                            <div className="flex justify-between items-center mb-6">
                                {/* Sort Dropdown */}
                                <div className="relative">
                                    <button
                                        onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                                        className="flex items-center gap-2 px-4 py-1.5 border border-[#EDEDED] rounded-lg text-xs font-medium text-[#515151] hover:bg-gray-50"
                                    >
                                        {sortBy === 'newest' ? 'Newest' : 'Oldest'} <ChevronDown size={14} />
                                    </button>

                                    {sortDropdownOpen && (
                                        <div className="absolute left-0 mt-2 w-32 bg-white border border-[#EDEDED] rounded-lg shadow-lg z-10">
                                            <button
                                                onClick={() => {
                                                    setSortBy('newest');
                                                    setSortDropdownOpen(false);
                                                }}
                                                className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                                            >
                                                Newest
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setSortBy('oldest');
                                                    setSortDropdownOpen(false);
                                                }}
                                                className="block w-full px-4 py-2 text-left text-sm hover:bg-gray-50"
                                            >
                                                Oldest
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Invite Admin Button - Only show on Admins tab */}
                                {activeTab === 'admins' && (
                                    <button
                                        onClick={() => setShowInviteModal(true)}
                                        className="px-4 py-1.5 bg-[#015FA6] text-white rounded-lg hover:bg-[#014a85] transition-colors text-xs font-medium flex items-center gap-2"
                                    >
                                        <UserPlus size={14} />
                                        Invite Admin
                                    </button>
                                )}
                            </div>

                            {/* Users List */}
                            <div className="space-y-3 flex-1">
                                {isLoading ? (
                                    <div className="flex justify-center items-center py-20">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#015FA6]"></div>
                                    </div>
                                ) : users.length === 0 ? (
                                    <div className="flex-1 flex flex-col items-center justify-center min-h-[400px]">
                                        <svg className="w-64 h-64 mb-6 text-[#A5A5A5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                        </svg>
                                        <p className="text-[#A5A5A5] text-sm font-medium">
                                            {activeTab === 'admins'
                                                ? 'No admin users yet. Invite your first admin to get started.'
                                                : `There are no ${activeTab === 'all' ? 'users' : activeTab} here yet`}
                                        </p>
                                        {activeTab === 'admins' && (
                                            <button
                                                onClick={() => setShowInviteModal(true)}
                                                className="mt-4 px-6 py-2 bg-[#015FA6] text-white rounded-lg hover:bg-[#014a85] transition-colors inline-flex items-center gap-2"
                                            >
                                                <UserPlus size={18} />
                                                Invite Admin
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    users.map((user) => {
                                        const isExpanded = expandedCards.has(user.id);
                                        const fullName = `${user.firstName} ${user.lastName}`;
                                        const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();

                                        return (
                                            <div
                                                key={user.id}
                                                className="flex items-center justify-between p-4 border border-[#EDEDED] rounded-xl hover:shadow-sm transition-shadow bg-white cursor-pointer group"
                                            >
                                                <div className="flex items-center gap-4">
                                                    {/* Avatar */}
                                                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl bg-[#015FA6]">
                                                        {initials}
                                                    </div>

                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-2">
                                                            <h3 className="text-base font-bold text-[#0B111E]">{fullName}</h3>
                                                            <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getRoleColor(user.role)}`}>
                                                                {getRoleLabel(user.role)}
                                                            </span>
                                                        </div>
                                                        <p className="text-xs text-[#A5A5A5]">{user.email}</p>
                                                        <div className="mt-1 flex items-center gap-2">
                                                            {getStatusBadge(user.isActive, user.profileCompleted)}
                                                            <span className="text-xs text-[#A5A5A5] flex items-center gap-1">
                                                                <Calendar size={12} />
                                                                Joined {formatDate(user.createdAt)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <button
                                                    onClick={() => toggleCardExpansion(user.id)}
                                                    className="text-[#A5A5A5] group-hover:text-[#015FA6] transition-colors p-2"
                                                >
                                                    <ChevronDown size={20} className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                                </button>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
            </div>

            {/* Invite Admin Modal */}
            {showInviteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                        <div className="p-6 border-b border-[#EDEDED]">
                            <h2 className="text-xl font-semibold text-[#0B111E]">Invite New Admin</h2>
                            <p className="text-sm text-[#757575] mt-1">
                                Send an invitation to join as platform administrator
                            </p>
                        </div>

                        <form onSubmit={handleInviteAdmin} className="p-6 space-y-4">
                            {inviteError && (
                                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                                    {inviteError}
                                </div>
                            )}

                            {inviteSuccess && (
                                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                                    {inviteSuccess}
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-[#0B111E] mb-2">First Name</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={inviteFormData.firstName}
                                    onChange={handleInviteInputChange}
                                    required
                                    className="w-full px-4 py-2 border border-[#EDEDED] rounded-lg focus:outline-none focus:border-[#015FA6]"
                                    placeholder="Enter first name"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-[#0B111E] mb-2">Last Name</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={inviteFormData.lastName}
                                    onChange={handleInviteInputChange}
                                    required
                                    className="w-full px-4 py-2 border border-[#EDEDED] rounded-lg focus:outline-none focus:border-[#015FA6]"
                                    placeholder="Enter last name"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-[#0B111E] mb-2">Email Address</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={inviteFormData.email}
                                    onChange={handleInviteInputChange}
                                    required
                                    className="w-full px-4 py-2 border border-[#EDEDED] rounded-lg focus:outline-none focus:border-[#015FA6]"
                                    placeholder="Enter email address"
                                />
                                <p className="text-xs text-[#757575] mt-1">
                                    An invitation email will be sent with password setup instructions
                                </p>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowInviteModal(false);
                                        setInviteError(null);
                                        setInviteSuccess(null);
                                        setInviteFormData({ firstName: '', lastName: '', email: '' });
                                    }}
                                    disabled={inviteLoading}
                                    className="flex-1 px-4 py-2 border border-[#CBCBCB] text-[#757575] rounded-lg hover:bg-[#F1F3F4] transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={inviteLoading}
                                    className="flex-1 px-4 py-2 bg-[#015FA6] text-white rounded-lg hover:bg-[#014a85] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {inviteLoading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Mail size={16} />
                                            Send Invitation
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default UserManagement;
